import React, { useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import DOMPurify from 'dompurify';
import { ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import BlogPostSEO from '../components/sections/BlogPostSEO';
import BlogPostHeader from '../components/sections/BlogPostHeader';
import { useBlogPost } from '../hooks/useBlog';

const NotFoundView = () => (
  <div className="min-h-screen flex items-center justify-center">
    <div className="text-center space-y-4">
      <h1 className="text-3xl font-bold text-gray-900">Post Not Found</h1>
      <p className="text-gray-600">
        This article isn&apos;t available yet — please check back soon.
      </p>
      <Link to="/blog">
        <Button>Back to Blog</Button>
      </Link>
    </div>
  </div>
);

const LoadingView = () => (
  <div className="min-h-screen flex items-center justify-center">
    <p className="text-gray-500">Loading article…</p>
  </div>
);

const PostCta = () => (
  <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-blue-600 to-blue-700 text-white">
    <div className="max-w-4xl mx-auto text-center space-y-10">
      <h2 className="text-3xl sm:text-4xl font-bold">
        Ready to Experience Professional Support?
      </h2>
      <p className="text-xl text-blue-100">
        Contact us today to learn how our nurse companion services can help you or your loved one
      </p>
      <div className="pt-4">
        <Link to="/contact">
          <Button size="lg" className="bg-white text-blue-600 hover:bg-blue-50 text-lg px-8 py-6">
            Book Your Consultation Now
            <ArrowRight className="w-5 h-5 ml-2" />
          </Button>
        </Link>
      </div>
    </div>
  </section>
);

const BlogPost = () => {
  const { slug } = useParams();
  const { post, isLoading, notFound } = useBlogPost(slug);

  const sanitizedContent = useMemo(
    () => (post ? DOMPurify.sanitize(post.content_html) : ''),
    [post]
  );

  if (isLoading) return <LoadingView />;
  if (notFound || !post) return <NotFoundView />;

  return (
    <div className="min-h-screen">
      <BlogPostSEO post={post} />
      <BlogPostHeader post={post} />

      {post.image && (
        <section className="px-4 sm:px-6 lg:px-8 py-8">
          <div className="max-w-4xl mx-auto">
            <img
              src={post.image}
              alt={post.title}
              className="w-full h-96 object-cover rounded-xl shadow-lg"
            />
          </div>
        </section>
      )}

      <section className="px-4 sm:px-6 lg:px-8 pb-20">
        <div className="max-w-4xl mx-auto">
          <div
            className="prose prose-lg max-w-none blog-content"
            dangerouslySetInnerHTML={{ __html: sanitizedContent }}
          />
        </div>
      </section>

      <PostCta />
    </div>
  );
};

export default BlogPost;
